import React, { useContext, ReactElement } from 'react';
import find from 'lodash/find';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import set from 'lodash/set';
import styled from 'styled-components';

import { Checkbox as ADCheckbox, ControlDescription } from 'src/presentation/components/agent_desktop_components';
import FormContext from 'src/presentation/contexts/FormContext';
import { StyledLabel } from '@ad-scripts/presentation/components/common/checkbox';

enum CheckboxSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

type CheckboxProps = {
  'data-aid'?: string;
  attr: string;
  checked?: boolean;
  className?: string;
  description?: string;
  disabled?: boolean;
  label: string | ReactElement;
  onChange?: Function;
  size?: CheckboxSize;
  value?: string;
};

const Checkbox = ({
  'data-aid': dataAid,
  attr,
  className,
  description,
  disabled,
  label,
  onChange,
  size = CheckboxSize.SMALL,
  value,
}: CheckboxProps) => {
  const { errors, workingState, updateState, resetError } = useContext(FormContext);
  const error = find(errors, error => error.attr === attr);
  const val = !isNil(value) ? value : get(workingState, attr);

  function changeHandler(checked) {
    if (onChange) {
      onChange(checked);
    } else {
      resetError({ attr, errors });
      updateState(set({}, attr, checked));
    }
  }

  const renderDescription = description ? <CheckboxControlDescription>{description}</CheckboxControlDescription> : null;

  const renderCheckbox = (
    <ADCheckbox
      data-aid={dataAid}
      error={get(error, 'detail')}
      label={label}
      onChange={() => changeHandler(!val)}
      disabled={disabled}
      checked={!!val}
      value={val ? 'checked' : ''}
      size={size}
      wrapperClassName={className}
    />
  );

  return (
    <CheckboxContainer>
      {renderCheckbox}
      {renderDescription}
    </CheckboxContainer>
  );
};

export const CheckboxWithBoldLabel = styled(Checkbox)`
  ${StyledLabel} {
    font-weight: ${p => p.theme.fontWeight.heavy};
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CheckboxControlDescription = styled(ControlDescription)`
  margin-bottom: 0;
  margin-left: ${p => p.theme.spacing.insetLarge};
  padding-top: ${p => p.theme.spacing.stackXSmall};
`;

export default Checkbox;
